import { classNames } from 'tradera/utils/class-names';
import type React from 'react';
import { forwardRef } from 'react';
import type { PolyRefFunction, OnlyAs } from 'react-polymorphed';

import styles from './heading.module.scss';

const polyRef = forwardRef as PolyRefFunction;

type HeadingSize =
    | 'stockholm'
    | 'madrid'
    | 'berlin'
    | 'london'
    | 'amsterdam'
    | 'paris';

const HeadingSizesMap: Record<string, HeadingSize> = {
    h1: 'stockholm', // default: 35, large: 42
    h2: 'madrid', // default: 21, large: 28
    h3: 'berlin', // default: 16, large: 21
    h4: 'london', // default: 16, large: 16
    h5: 'paris', // default: 14, large: 14
};

export type HeadingProps = {
    /**
     * You don't necessarily need to use this prop, setting `as` should get you a good default size.
     * You can override the size from `as` by using this prop.
     *
     * - stockholm: small screen: 35px, large screen: 42px
     * - madrid: small screen: 21px, large screen: 28px
     * - berlin: small screen: 16px, large screen: 21px
     * - london: small screen: 16px, large screen: 16px
     * - paris: small screen: 14px, large screen: 14px
     */
    size?: HeadingSize;
    className?: string;
    /**
     * Defaults to `left`
     */
    align?: 'left' | 'center' | 'right';
    branded?: boolean;
    children?: React.ReactNode;
};

/**
 * Heading component
 *
 * Use this for setting creating heading elements. Use "as" to set semantically correct heading level, and use "size" to override the default size per heading level.
 *
 * @example
 * <Heading as="h1">
 *    My heading
 * </Heading>
 *
 * @example
 * <Heading as="h1" align="center" branded>
 *    A centered branded heading
 * </Heading>
 */
export const Heading = polyRef<
    'h1',
    HeadingProps,
    OnlyAs<'h1' | 'h2' | 'h3' | 'h4' | 'h5'>
>(
    (
        {
            as: Component = 'h1',
            size: sizeProp,
            children,
            align,
            branded = false,
            className,
            ...props
        },
        ref,
    ) => {
        const size = (sizeProp ||
            HeadingSizesMap[Component as HeadingSize]) as HeadingSize;

        return (
            <Component
                className={classNames(
                    styles.reset,
                    {
                        [`size-${size}`]: size,
                        [`text-${align}`]: align,
                        'text-styled': branded,
                    },
                    className,
                )}
                ref={ref}
                {...props}>
                {children}
            </Component>
        );
    },
);
